import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { getAccessToken } from "@/lib/storeManager";

/**
 * Short-lived GET cache + in-flight dedupe for the Jain eLibrary axios instance.
 *
 * First load of each URL still depends on server/network latency (optimize the API for “ms”
 * response times). The browser can still: dedupe parallel identical GETs, serve repeats from
 * memory within TTL, avoid redundant tab fetches, and trim DevTools console overhead.
 */
const TTL_MS = 15_000;
const MAX_ENTRIES = 250;

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<AxiosResponse>>();

type CacheEntry = {
  data: unknown;
  status: number;
  statusText: string;
  headers: unknown;
  until: number;
};

/** Paths where stale reads are unacceptable or mutations are common. */
const BYPASS_SUBSTRINGS = [
  "/cart/",
  "/checkout",
  "/paypal",
  "/auth/",
  "token-refresh",
  "/site-content-pages/",
  "/shopping-orders",
];

export function clearJainElibraryGetCache(): void {
  cache.clear();
  inflight.clear();
}

function shouldBypass(url: string): boolean {
  const u = url.toLowerCase();
  return BYPASS_SUBSTRINGS.some(s => u.includes(s));
}

function stableParamsKey(params: unknown): string {
  if (params === null || params === undefined || params === "") {
    return "";
  }
  if (typeof params !== "object") {
    return String(params);
  }
  const o = params as Record<string, unknown>;
  const keys = Object.keys(o).sort();
  return JSON.stringify(keys.map(k => [k, o[k]]));
}

function cacheKey(url: string, config?: AxiosRequestConfig): string {
  const params = stableParamsKey(config?.params);
  /** Session token from storage so keys match whether callers pass `headers` or rely on interceptors. */
  const session = getAccessToken() ?? "";
  return `GET ${url} ${params} sid:${session}`;
}

function deepClone<T>(data: T): T {
  return JSON.parse(JSON.stringify(data)) as T;
}

function trimCache(): void {
  if (cache.size <= MAX_ENTRIES) {
    return;
  }
  const now = Date.now();
  const entries = [...cache.entries()].sort((a, b) => a[1].until - b[1].until);
  for (const [k, v] of entries) {
    if (v.until <= now || cache.size <= MAX_ENTRIES * 0.75) {
      cache.delete(k);
    }
    if (cache.size <= MAX_ENTRIES * 0.75) {
      break;
    }
  }
}

export type JainGetConfig = AxiosRequestConfig & { cache?: boolean };

/**
 * Wraps `instance.get` only (all service calls in this project use `.get` for reads).
 */
export function attachJainElibraryGetCaching(instance: AxiosInstance): void {
  const rawGet = instance.get.bind(instance);

  const getCached = function getCached<
    T = any,
    R extends AxiosResponse<T> = AxiosResponse<T>,
    D = any,
  >(url: string, config?: JainGetConfig): Promise<R> {
    if (config?.cache === false || shouldBypass(url)) {
      return rawGet<T, R, D>(url, config);
    }

    const key = cacheKey(url, config);
    const now = Date.now();
    const hit = cache.get(key);
    if (hit && hit.until > now) {
      return Promise.resolve({
        data: deepClone(hit.data) as T,
        status: hit.status,
        statusText: hit.statusText,
        headers: hit.headers,
        config: config as AxiosResponse<T>["config"],
        request: undefined,
      } as R);
    }

    const pending = inflight.get(key);
    if (pending) {
      return pending as Promise<R>;
    }

    const p = rawGet<T, R, D>(url, config)
      .then((res: AxiosResponse<T>) => {
        inflight.delete(key);
        if (res.status >= 200 && res.status < 300) {
          trimCache();
          cache.set(key, {
            data: res.data,
            status: res.status,
            statusText: res.statusText,
            headers: res.headers,
            until: Date.now() + TTL_MS,
          });
        }
        return res as R;
      })
      .catch(err => {
        inflight.delete(key);
        throw err;
      });

    inflight.set(key, p as Promise<AxiosResponse<T>>);
    return p as Promise<R>;
  };

  instance.get = getCached as AxiosInstance["get"];
}
